import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, mediaApi, mediaUrl, type MediaTarget } from '../api';
import ImageCropper from './ImageCropper';

type Props = {
  target: MediaTarget;
  /** 0 (eller undefined) betyder "staging mode" — billedet gemmes i state og uploades senere via onStaged */
  targetId?: number;
  /** Mindre størrelse til kompakte forms (kategori/placering) */
  compact?: boolean;
  /**
   * Staging mode (når targetId ikke er sat): kaldes når brugeren vælger og
   * beskærer et billede. Forældrekomponenten gemmer blob'en og uploader den
   * efter at have oprettet target-entiteten.
   */
  onStaged?: (blob: Blob | null) => void;
  /** I staging mode: blob fra staging — vises som preview */
  stagedBlob?: Blob | null;
};

/**
 * Single-image upload til kategorier og placeringer. Erstatter eksisterende
 * billede automatisk når et nyt uploades. Backend håndhæver max 1 billede.
 *
 * Når targetId er 0/undefined (fx ved oprettelse hvor entiteten ikke har
 * et ID endnu), kører komponenten i "staging mode": billedet gemmes via
 * onStaged-callback og uploades senere af forældrekomponenten.
 */
export default function SingleImageUpload({
  target,
  targetId = 0,
  compact = false,
  onStaged,
  stagedBlob = null,
}: Props) {
  const isStagingMode = targetId === 0;
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [pendingFilename, setPendingFilename] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const queryKey = ['media', target, targetId];
  const queryFn =
    target === 'items'
      ? () => mediaApi.listForItem(targetId)
      : target === 'categories'
        ? () => mediaApi.listForCategory(targetId)
        : () => mediaApi.listForLocation(targetId);

  const { data: list = [] } = useQuery({
    queryKey,
    queryFn,
    enabled: !isStagingMode && targetId > 0,
  });
  const photo = list.find((m) => m.kind === 'photo');

  // Preview-URL i staging mode (ikke uploadet endnu)
  const stagedUrl = stagedBlob ? URL.createObjectURL(stagedBlob) : null;

  const uploadMutation = useMutation({
    mutationFn: (args: { blob: Blob; filename: string }) =>
      mediaApi.upload(target, targetId, args.blob, 'photo', args.filename),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ['storage-usage'] });
      setError(null);
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? t([`error.${err.message}`, 'error.UNKNOWN']) : t('error.UNKNOWN'),
      );
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => mediaApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ['storage-usage'] });
    },
  });

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result as string);
      setPendingFilename(file.name);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  function handleCropComplete(blob: Blob) {
    if (isStagingMode) {
      // Vi har ikke et ID endnu — giv blob'en til forældrekomponenten
      onStaged?.(blob);
    } else {
      uploadMutation.mutate({ blob, filename: pendingFilename ?? 'upload.jpg' });
    }
    setCropSrc(null);
    setPendingFilename(null);
  }

  function handleStagedRemove() {
    onStaged?.(null);
  }

  const size = compact ? 'w-20 h-20' : 'w-32 h-32';

  return (
    <div className="space-y-2">
      {error && <div className="rounded bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}

      {cropSrc && (
        <ImageCropper
          src={cropSrc}
          aspect={1}
          onCancel={() => {
            setCropSrc(null);
            setPendingFilename(null);
          }}
          onCrop={handleCropComplete}
        />
      )}

      {!cropSrc && (
        <div className="flex items-start gap-3">
          {/* Eksisterende uploadet billede (kun i ikke-staging mode) */}
          {!isStagingMode && photo && (
            <div className="relative group">
              <a href={mediaUrl(photo)} target="_blank" rel="noopener noreferrer">
                <img
                  src={mediaUrl(photo, { thumbnail: !!photo.thumbnailKey })}
                  alt=""
                  className={`${size} object-cover rounded border border-slate-200`}
                />
              </a>
              <button
                type="button"
                onClick={() => {
                  if (confirm(t('media.confirmDelete'))) removeMutation.mutate(photo.id);
                }}
                className="absolute -top-2 -right-2 bg-white text-red-600 hover:text-red-800 rounded-full w-6 h-6 flex items-center justify-center text-sm shadow border border-slate-200"
                title={t('common.delete')}
              >
                ×
              </button>
            </div>
          )}

          {/* Staged billede preview (klar til at blive uploadet ved gem) */}
          {isStagingMode && stagedUrl && (
            <div className="relative group">
              <img
                src={stagedUrl}
                alt=""
                className={`${size} object-cover rounded border border-amber-300`}
              />
              <button
                type="button"
                onClick={handleStagedRemove}
                className="absolute -top-2 -right-2 bg-white text-red-600 hover:text-red-800 rounded-full w-6 h-6 flex items-center justify-center text-sm shadow border border-slate-200"
                title={t('common.delete')}
              >
                ×
              </button>
              <span className="absolute bottom-0 left-0 right-0 bg-amber-100/95 text-amber-900 text-[10px] text-center py-0.5 rounded-b">
                {t('media.staged')}
              </span>
            </div>
          )}

          {/* + knap når der ikke er noget billede */}
          {!photo && !stagedUrl && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className={`${size} rounded border-2 border-dashed border-slate-300 hover:border-slate-500 text-slate-500 hover:text-slate-700 flex flex-col items-center justify-center text-xs disabled:opacity-50`}
            >
              <span className="text-2xl leading-none">+</span>
              <span className="mt-1">{t('media.addImage')}</span>
            </button>
          )}

          <div className="text-xs text-slate-500 pt-1">
            {isStagingMode && stagedUrl
              ? t('media.stagedHint')
              : photo
                ? t('media.singleImageReplace')
                : t('media.singleImageHint')}
            {(photo || (isStagingMode && stagedUrl)) && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="ml-2 text-slate-700 hover:underline"
              >
                {t('media.replace')}
              </button>
            )}
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}
